import { CartAttributes } from "./cart.model";
import { TableColumn } from "./data-grid.model";
import { ReviewAttributes } from "./review.model";

export interface ProductBody {
    id: number;
    name: string;
    price: number;
    discountedPrice?: number | null;
    description: string;
    images: Images;
    quantity?: number;
    value?: number;
    video?: string[];
    isActive?: boolean;
}

export interface CartDetails {
    product: ProductAttributes;
    singleBlockInfo?: SingleBlockInfo | null;
    cartUUID?: string;
}

export interface SingleBlockInfo {
    color: object;
    front?: string;
    back?: string;
    left?: string;
    right?: string;
    top?: string;
    bottom?: string;
}

export interface StructureModelInfo {
    modelInfoUrl: string;
    totalBlock: number;
}

export interface BaseProduct {
    name: string;
    price: number;
    discountedPrice?: number | null;
    discountPercentage?: number;
    images: Images;
    video?: string[];
    isActive: boolean;
    gstRate: number;
    description: string;
    glbUrl?: string;
    model3DJson?: object;
    modelQuantityJson?: StructureModelInfo | null;
    createdBy?: string;
    updatedBy?: string;
    createdByEmail?: string;
    updatedByEmail?: string;
    category: string[];
    subCategory: string | null;
    bestSellerCategoryAt?: Date;
    // offerCategory?: string;
    // coupon?: object;
    averageRating?: number;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProductAttributes extends BaseProduct {
    id: number;
    quantity?: number;
    cart?: CartAttributes;
    singleBlockInfo?: SingleBlockInfo | null;
    reviews?: ReviewAttributes[];
    disable?: boolean;
    isStructureUpdate?: boolean;
    structureId?: number;
}

export interface Images {
    imagesUrl: string[];
}

export interface ProductsWithTotalAmount {
    products: ProductAttributes[];
    totalAmount: number;
    totalCount?: number;
}

export interface ReviewValues {
    productId: number;
    orderId: number;
    rating: number;
    review?: string;
    status: string;
    name: string;
    images?: Images;
    rejectComment?: string;
    userId: number;
}

export enum RatingResponse {
    Added = "Review added successfully",
}

export enum ErrorMessagesForProductOperation {
    Failed = "Failed to add product",
    FailedToUpload = "Failed to upload to S3",
    FailedUpdate = "Failed to update product",
    FailedGetById = "Failed to get product by id",
    NotFound = "Product not found",
    NotFoundExistingProduct = "Existing product not found",
    NotAnArray = "Parsed video is not an array",
    FailedFetch = "Failed to fetch products",
    FailedFetchInactiveProductIds = "Failed to fetch inactive product ids",
    FailedAddReview = "Failed to add review",
    FailedFetchSuggestedProducts = "Failed to fetch suggested products",
    FailedToAddCoupon = "Failed to add Coupon",
    FailedToFetchCouponDetails = "Failed to Fetch coupon Details",
}

export enum FileErrorMessage {
    Fileconfiguration = "Invalid maximum file size configuration",
    NoFile = "No files selected",
    ImageFilesize = "Each image must be less than 2MB",
    ImageMimeType = "Only JPG and PNG files are allowed",
    VideoFileSize = "The video must be less than 20MB",
    VideoFile = "Only video files are allowed",
    VideoMimeType = "only .mp4 and .webM are allowed",
    Model3dFormat = "Invalid JSON format for 3D model",
    ModelQuantityJsonFormat = "Invalid JSON format for quantity model.",
}

export const productListColumns: TableColumn[] = [
    // { key: "checkbox", value: "Checkbox", type: "checkbox" },
    { key: "action", value: "Action", type: "action" },
    { key: "images", value: "Image", type: "thumbnail" },
    { key: "name", value: "Name", type: "text" },
    { key: "category", value: "Category", type: "text" },
    { key: "description", value: "Description", type: "text" },
    { key: "price", value: "Price", type: "price" },
    { key: "discountedPrice", value: "Discounted Price", type: "price" },
    { key: "gstRate", value: "GST Rate", type: "percent" },
    { key: "isActive", value: "Is Active", type: "boolean" },
    { key: "createdBy", value: "Created By", type: "text", tooltipKey: "createdByEmail" },
    { key: "updatedBy", value: "Updated By", type: "text", tooltipKey: "updatedByEmail" },
    { key: "createdAt", value: "Created At", type: "datetime" },
    { key: "updatedAt", value: "Updated At", type: "datetime" },
];

export enum ProductResponse {
    Added = "Product added successfully",
    Updated = "Product updated successfully",
}

export enum ProductCategories {
    Featured = "Featured",
    OnSale = "On Sale",
    BestSeller = "Best Seller",
    BuyBlocks = "Buy Blocks",
    All = "All Products",
}

export const productCategories = [
    ProductCategories.Featured,
    ProductCategories.BestSeller,
    ProductCategories.BuyBlocks,
    ProductCategories.OnSale,
];

export enum ProductQueryCategory {
    Featured = "featured",
    Sale = "sale",
    BestSeller = "bestSeller",
    Block = "block",
}

export interface Specifications {
    label: string;
    value: string;
    isDynamic?: boolean; // Optional property (only needed for dynamic values)
}

export enum SpecificationsLabel {
    TotalBlocks = "Total Blocks",
    AssemblyRequired = "Assembly Required",
    MaterialType = "Material Type",
    Colour = "Colour",
    Dimensions = "Dimensions (LxWxH)",
    ItemWeight = "Item Weight",
    NumberOfShape = "Number of Blocks",
    CountryOfOrigin = "Country of Origin",
    IdealFor = "Ideal For",
    MinimumAge = "Minimum Age",
}

export const SpecificationData: Specifications[] = [
    { label: SpecificationsLabel.TotalBlocks, value: "", isDynamic: true },
    { label: SpecificationsLabel.AssemblyRequired, value: "Yes" },
    { label: SpecificationsLabel.MaterialType, value: "Plastic" },
    { label: SpecificationsLabel.Colour, value: "Multicolour" },
    { label: SpecificationsLabel.Dimensions, value: "10 x 5 x 15 inches" },
    { label: SpecificationsLabel.ItemWeight, value: "2kg" },
    { label: SpecificationsLabel.NumberOfShape, value: "productDetails.shapeDetail", isDynamic: true },
    { label: SpecificationsLabel.CountryOfOrigin, value: "USA" },
    { label: SpecificationsLabel.IdealFor, value: "Boys & Girls" },
    { label: SpecificationsLabel.MinimumAge, value: "4 Year" },
];

export interface DiscountRange {
    label: string;
    value: number;
}
